import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, IconButton, cx } from './core.tsx'

// ------------------------------------------------------------------- Alert

export type AlertTone = 'info' | 'success' | 'warning' | 'danger'

const ALERT_ICON: Record<AlertTone, string> = {
  info: 'fas fa-info-circle',
  success: 'fas fa-check-circle',
  warning: 'fas fa-exclamation-triangle',
  danger: 'fas fa-exclamation-circle',
}

export function Alert({
  tone = 'info',
  title,
  children,
  action,
}: {
  tone?: AlertTone
  title?: string
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div className={cx('ob-alert', `ob-alert--${tone}`)} role={tone === 'danger' ? 'alert' : 'status'}>
      <i className={ALERT_ICON[tone]} aria-hidden style={{ marginTop: 2 }} />
      <div className="ob-grow">
        {title ? <div className="ob-alert__title">{title}</div> : null}
        <div>{children}</div>
      </div>
      {action}
    </div>
  )
}

// -------------------------------------------------------------- EmptyState

/**
 * Estado vazio.
 *
 * `description` e `action` nao sao opcionais por acaso: a spec 5.5 exige que
 * todo empty state explique o que aconteceu e ofereca o proximo passo. Um icone
 * com "Nada aqui" deixa o usuario sem saida.
 */
export function EmptyState({
  icon = 'fas fa-inbox',
  title,
  description,
  action,
}: {
  icon?: string
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="ob-empty">
      <div className="ob-empty__icon">
        <i className={icon} aria-hidden />
      </div>
      <div className="ob-empty__title">{title}</div>
      <p className="ob-empty__description">{description}</p>
      {action}
    </div>
  )
}

// ------------------------------------------------------------ LoadingState

export function Skeleton({ width, height = 14, radius }: { width?: number | string; height?: number; radius?: number }) {
  return (
    <span
      className="ob-skeleton"
      style={{ display: 'block', width: width ?? '100%', height, borderRadius: radius }}
      aria-hidden
    />
  )
}

/**
 * Skeleton com a forma da tela que esta carregando (spec 5.5).
 *
 * `rows` imita uma lista; `variant="board"` imita colunas de Kanban. Manter o
 * esqueleto parecido com o resultado evita o salto de layout quando os dados
 * chegam.
 */
export function LoadingState({
  rows = 5,
  variant = 'list',
  label = 'Carregando',
}: {
  rows?: number
  variant?: 'list' | 'board' | 'detail'
  label?: string
}) {
  return (
    <div className="ob-stack" role="status" aria-live="polite" aria-busy="true">
      <span className="ob-sr-only">{label}</span>
      {variant === 'board' ? (
        <div style={{ display: 'flex', gap: 12 }}>
          {Array.from({ length: 4 }, (_, column) => (
            <div key={column} className="ob-stack" style={{ flex: 1 }}>
              <Skeleton height={28} />
              {Array.from({ length: rows }, (_, card) => (
                <Skeleton key={card} height={72} radius={12} />
              ))}
            </div>
          ))}
        </div>
      ) : variant === 'detail' ? (
        <>
          <Skeleton width="45%" height={24} />
          <Skeleton width="70%" />
          <Skeleton height={120} radius={12} />
        </>
      ) : (
        Array.from({ length: rows }, (_, i) => <Skeleton key={i} height={48} radius={10} />)
      )}
    </div>
  )
}

// ------------------------------------------------------------------- Toast

export type ToastTone = 'info' | 'success' | 'warning' | 'danger'
export type Toast = { id: string; tone: ToastTone; message: string; action?: { label: string; onClick: () => void } }

type ToastContextValue = {
  toasts: readonly Toast[]
  push: (toast: Omit<Toast, 'id'>) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

/** Toasts no canto inferior direito (spec 5.5). */
export function ToastProvider({ children, timeout = 5000 }: { children: ReactNode; timeout?: number }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  // Mouse sobre a mensagem SEGURA o fechamento; ao sair, o timer recomeca
  // (pedido do usuario 2026-08-24).
  const [pausado, setPausado] = useState(false)

  const dismiss = useCallback((id: string) => setToasts((list) => list.filter((t) => t.id !== id)), [])

  const push = useCallback((toast: Omit<Toast, 'id'>) => {
    setToasts((list) => [...list, { ...toast, id: crypto.randomUUID() }])
  }, [])

  useEffect(() => {
    if (toasts.length === 0) {
      // ultimo toast fechado no X com o mouse em cima: o mouseleave nao
      // dispara (o elemento desmonta) — destrava aqui
      setPausado(false)
      return
    }
    if (pausado) return
    // Um timer por toast: um timer unico derrubaria os mais novos junto.
    const timers = toasts.map((toast) => setTimeout(() => dismiss(toast.id), timeout))
    return () => timers.forEach(clearTimeout)
  }, [toasts, timeout, dismiss, pausado])

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document === 'undefined'
        ? null
        : createPortal(
            <div
              className="ob-toasts"
              role="region"
              aria-label="Notificações"
              aria-live="polite"
              onMouseEnter={() => setPausado(true)}
              onMouseLeave={() => setPausado(false)}
            >
              {toasts.map((toast) => (
                <div key={toast.id} className={cx('ob-toast', `ob-toast--${toast.tone}`)}>
                  <i className={ALERT_ICON[toast.tone]} aria-hidden />
                  <span className="ob-grow">{toast.message}</span>
                  {toast.action ? (
                    <Button variant="ghost" size="sm" onClick={toast.action.onClick}>
                      {toast.action.label}
                    </Button>
                  ) : null}
                  <IconButton icon="fas fa-times" label="Fechar" size="sm" onClick={() => dismiss(toast.id)} />
                </div>
              ))}
            </div>,
            document.body,
          )}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast precisa estar dentro de <ToastProvider>')
  return context
}
